import {
  createContext,
  createElement,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useSearchParams } from 'react-router-dom';

import {
  defaultQueryParamKeys,
  DateRangeFilter,
  determineCorrectDateFromPresetOrRange,
  type RangePickerQueryParamKeys,
  type TimeRangeConstraints,
} from '@cast/shared-date-ui';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  getResourceUtilizationStep,
  ResourceUtilizationChartType,
} from '@cast/features-cluster-shared-utils';

dayjs.extend(utc);
dayjs.extend(timezone);

export const CLUSTER_DASHBOARD_QUERY_PARAM_KEYS: RangePickerQueryParamKeys =
  defaultQueryParamKeys;

export const CLUSTER_DASHBOARD_FILTER_PRESETS = [
  DateRangeFilter.Last15Mins,
  DateRangeFilter.Last30Mins,
  DateRangeFilter.LastHour,
  DateRangeFilter.Last24Hours,
  DateRangeFilter.Last7Days,
  DateRangeFilter.Last30Days,
];

export const CLUSTER_DASHBOARD_DEFAULT_PRESET = DateRangeFilter.Last24Hours;

type UseClusterDashboardTimeRangeArgs = {
  initialPreset?: DateRangeFilter;
  timeZone?: string;
  allowTimeInput?: boolean;
};

export type ClusterDashboardTimeRangeValue = {
  from: string | undefined;
  to: string | undefined;
  preset: DateRangeFilter | undefined;
  timeZone: string;
  getRange: () => ReturnType<typeof determineCorrectDateFromPresetOrRange>;
  range: ReturnType<typeof determineCorrectDateFromPresetOrRange>;
  key: string;
  constraints: TimeRangeConstraints;
  /** Frozen once per URL/preset so all overview consumers share one RQ key. */
  overviewQueryArgs: {
    startTime: string;
    endTime: string;
    stepSeconds: number;
  };
};

const ClusterDashboardTimeRangeContext =
  createContext<ClusterDashboardTimeRangeValue | null>(null);

const useClusterDashboardTimeRangeState = ({
  initialPreset = CLUSTER_DASHBOARD_DEFAULT_PRESET,
  timeZone: timeZoneOverride,
  allowTimeInput = true,
}: UseClusterDashboardTimeRangeArgs = {}): ClusterDashboardTimeRangeValue => {
  const { cluster } = useCluster();
  const [searchParams] = useSearchParams();

  const from = searchParams.get(defaultQueryParamKeys.fromDate) || undefined;
  const to = searchParams.get(defaultQueryParamKeys.toDate) || undefined;
  const preset =
    searchParams.get(defaultQueryParamKeys.dateRangePreset) || undefined;
  const timeZone =
    searchParams.get(defaultQueryParamKeys.timeZone) ||
    timeZoneOverride ||
    dayjs.tz.guess();

  const constraints = useMemo<TimeRangeConstraints>(
    () => ({
      minDate: ({ timezone }) =>
        dayjs.utc(cluster.createdAt).tz(timezone).startOf('day').toISOString(),
      maxDate: () => dayjs.utc().toISOString(),
    }),
    [cluster.createdAt]
  );

  const getRange = useCallback(
    () =>
      determineCorrectDateFromPresetOrRange({
        preset: preset as DateRangeFilter | undefined,
        timezone: timeZone,
        from,
        to,
        fallbackPreset: initialPreset,
        allowTimeInput,
        constraints,
      }),
    [preset, from, to, timeZone, initialPreset, allowTimeInput, constraints]
  );

  const key = `${from}-${to}-${preset}-${timeZone}`;

  // Freeze relative preset boundaries until URL selection changes.
  // Without this, preset-based ranges recompute from `now()` each render and
  // destabilize React Query keys (request spam).
  const range = useMemo(() => getRange(), [getRange]);

  const overviewQueryArgs = useMemo(() => {
    const stepSeconds = getResourceUtilizationStep(
      range,
      ResourceUtilizationChartType.AREA
    );

    return {
      startTime: dayjs(range.from).tz(timeZone).toISOString(),
      endTime: dayjs(range.to).tz(timeZone).toISOString(),
      stepSeconds,
    };
  }, [range, timeZone]);

  return {
    from,
    to,
    preset: preset as DateRangeFilter | undefined,
    timeZone,
    getRange,
    range,
    key,
    constraints,
    overviewQueryArgs,
  };
};

export const ClusterDashboardTimeRangeProvider = ({
  children,
  ...args
}: PropsWithChildren<UseClusterDashboardTimeRangeArgs>) => {
  const value = useClusterDashboardTimeRangeState(args);

  return createElement(
    ClusterDashboardTimeRangeContext.Provider,
    { value },
    children
  );
};

export const useClusterDashboardTimeRange =
  (): ClusterDashboardTimeRangeValue => {
    const context = useContext(ClusterDashboardTimeRangeContext);

    if (!context) {
      throw new Error(
        'useClusterDashboardTimeRange must be used within ClusterDashboardTimeRangeProvider'
      );
    }

    return context;
  };
